import { memo, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Heart, Bookmark } from "lucide-react";
import api from "@/lib/api";
import { mockMatches } from "@/data/listings";
import { priceParts } from "@/lib/pricing";
import NavTop from "@/components/nav/nav_top";
import ButtonGhost from "@/components/buttons/button_ghost";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useSaves } from "@/context/SavesContext";

export default function MatchesPage() {
  const [matches, setMatches] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items: savedHomes } = useSaves();
  const { markSeen } = useNotifications();
  const isDemo = Boolean(user?.demo);

  // Viewing this page consumes the "new matches" notification counter.
  useEffect(() => {
    markSeen("matches");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get("/matches")
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : res.data?.matches;
          // Real accounts with no matches see the honest empty state.
          // Mock data is reserved for offline demo sessions only.
          if (alive) setMatches(prep(data?.length ? data : isDemo ? mockMatches : []));
        })
        .catch(() => alive && setMatches((m) => m ?? prep(isDemo ? mockMatches : [])));
    load();
    // WHY the 15s poll: a fresh match (or a new last message) should appear
    // while the user is sitting on this page, not only after a manual
    // reload. Hidden tabs skip the fetch to save battery and server load.
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [isDemo]);

  // The real API uses a last_message OBJECT; the offline demo mocks use a
  // string. Normalize both here — same rules as the Messages inbox.
  function prep(list) {
    return list.map((m) => ({
      ...m,
      preview: typeof m.last_message === "string" ? m.last_message : m.last_message?.body,
      ts: m.last_message_at || m.last_message?.created_at || m.created_at,
    }));
  }

  // Chat is free for every match (the old per-chat unlock is gone), so a
  // match row always opens its conversation directly.
  function openMatch(m) {
    navigate(`/chat/${m.id}`);
  }

  return (
    <div className="min-h-screen bg-night pb-24 md:pb-10">
      <NavTop />
      <main className="mx-auto max-w-3xl px-4 pt-8 sm:px-6">
        <h1 className="text-2xl font-semibold tracking-tight text-night-text">Matches</h1>
        <p className="mt-1 text-sm text-night-sub">
          Homes that liked you back. Chatting with your matches is always free.
        </p>

        {/* Saved homes — a peek strip; the full grid lives on /saved. */}
        {savedHomes.length > 0 && (
          <section data-testid="saved-strip" className="mt-6">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-1.5 text-sm font-semibold text-night-text">
                <Bookmark size={15} className="fill-brand-primary text-brand-primary" /> Saved homes
              </h2>
              <Link to="/saved" className="text-xs font-semibold text-brand-primary hover:underline">
                See all
              </Link>
            </div>
            <div className="no-scrollbar -mx-4 mt-3 flex gap-3 overflow-x-auto px-4 sm:-mx-6 sm:px-6">
              {savedHomes.map((l) => (
                <Link key={l.id} to={`/listing/${l.id}`} className="w-40 shrink-0">
                  <img
                    src={l.photos?.[0]}
                    alt={l.title}
                    loading="lazy"
                    decoding="async"
                    className="h-28 w-40 rounded-xl object-cover"
                  />
                  <p className="mt-1.5 truncate text-xs font-semibold text-night-text">{l.title}</p>
                  <p className="text-xs text-night-sub">
                    ${priceParts(l).amount.toLocaleString()} {priceParts(l).unit}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!matches ? (
          <div className="mt-16 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
          </div>
        ) : matches.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-night-line bg-night-surface p-10 text-center">
            <Heart className="mx-auto text-night-text" size={26} strokeWidth={1.6} />
            <h2 className="mt-3 text-lg font-semibold text-night-text">No matches yet</h2>
            <p className="mt-1 text-sm text-night-sub">Keep swiping. Your next home is out there.</p>
            <ButtonGhost className="mt-5" onClick={() => navigate("/swipe")}>Back to swiping</ButtonGhost>
          </div>
        ) : (
          <div className="mt-6 divide-y divide-night-line rounded-2xl border border-night-line bg-night-surface">
            {matches.map((m) => (
              <MatchRow key={m.id} match={m} onOpen={() => openMatch(m)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// Memoized list row — the matches list re-renders as data streams in, but
// each row only depends on its own match.
const MatchRow = memo(function MatchRow({ match: m, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-4 p-4 text-left transition hover:bg-night-tint"
    >
      <img
        src={m.listing?.photos?.[0]}
        alt={m.listing?.title}
        loading="lazy"
        decoding="async"
        className="h-16 w-16 shrink-0 rounded-xl object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="truncate text-[15px] font-medium text-night-text">{m.listing?.title}</h3>
          {m.ts && (
            <span className="shrink-0 text-xs text-night-sub">
              {formatDistanceToNow(new Date(m.ts), { addSuffix: true })}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-sm text-night-sub">{m.other_party?.name || m.listing?.lister_name}</p>
        <p className="mt-1 line-clamp-1 text-sm text-night-sub">
          {m.preview || "You matched. Say hello."}
        </p>
      </div>
      {m.unread > 0 && (
        <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-brand-primary px-1.5 text-xs font-semibold text-white">
          {m.unread}
        </span>
      )}
    </button>
  );
});
